'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export type Lang = 'th' | 'en';

const LANG_KEY = 'mystic_oracle_lang';
const PROFILE_KEY = 'mystic_oracle_profile';

interface LanguageContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  toggle: () => void;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  // IMPORTANT (hydration safety): the server render and the first client render
  // must be identical, so we always start from the same deterministic default
  // ('th', matching <html lang="th">). The real preference is read from
  // localStorage only AFTER mount, in the effect below.
  const [lang, setLangState] = useState<Lang>('th');

  useEffect(() => {
    try {
      const stored = localStorage.getItem(LANG_KEY);
      if (stored === 'th' || stored === 'en') {
        setLangState(stored);
        return;
      }
      // Fall back to a previously saved profile's language preference.
      const rawProfile = localStorage.getItem(PROFILE_KEY);
      if (rawProfile) {
        const p = JSON.parse(rawProfile);
        if (p?.language === 'th' || p?.language === 'en') setLangState(p.language);
      }
    } catch {
      /* ignore storage access errors */
    }
  }, []);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem(LANG_KEY, next);
      // Keep the saved profile in sync so the rest of the flow (and any saved
      // reading) reflects the chosen language too.
      const rawProfile = localStorage.getItem(PROFILE_KEY);
      if (rawProfile) {
        const p = JSON.parse(rawProfile);
        p.language = next;
        localStorage.setItem(PROFILE_KEY, JSON.stringify(p));
      }
    } catch {
      /* ignore storage access errors */
    }
  }, []);

  const toggle = useCallback(() => {
    setLang(lang === 'th' ? 'en' : 'th');
  }, [lang, setLang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider');
  return ctx;
}
